/**
 * AI 配置管理服务
 */

import { apiClient } from '../config/netConfig';
import type { ApiResponse } from '../types';

/**
 * 生成模拟组件数据
 */
function generateMockComponents(): Component[] {
  const mockComponents: Component[] = [
    {
      id: '1',
      serviceName: 'msCentral',
      pageId: 'group-business-data',
      componentId: 'revenueChart',
      name: '营收趋势图',
      type: 'chart',
      displayInfo: '显示集团营收趋势',
      description: '展示集团整体营收趋势的折线图',
      isActive: true,
      sortOrder: 1,
    },
    {
      id: '2',
      serviceName: 'msCentral',
      pageId: 'group-business-data',
      componentId: 'incomeTable',
      name: '收入明细表',
      type: 'table',
      displayInfo: '显示收入明细数据',
      description: '展示详细的收入数据表格',
      isActive: true,
      sortOrder: 2,
    },
    {
      id: '3',
      serviceName: 'msCentral',
      pageId: 'store-revenue-monitor',
      componentId: 'roomStatusChart',
      name: '房态分析图',
      type: 'chart',
      displayInfo: '显示房态分析',
      description: '展示房间状态的分析图表',
      isActive: true,
      sortOrder: 1,
    },
    {
      id: '4',
      serviceName: 'msCentral',
      pageId: 'store-revenue-monitor',
      componentId: 'orderTrafficChart',
      name: '订单流速图',
      type: 'chart',
      displayInfo: '显示订单流速趋势',
      description: '展示订单流速的趋势图表',
      isActive: true,
      sortOrder: 2,
    },
    {
      id: '5',
      serviceName: 'msCentral',
      pageId: 'store-user-analysis',
      componentId: 'userAnalysisChart',
      name: '用户分析图',
      type: 'chart',
      displayInfo: '显示用户分析数据',
      description: '展示用户分析相关的图表',
      isActive: true,
      sortOrder: 1,
    },
    {
      id: '6',
      serviceName: 'msCentral',
      pageId: 'store-user-analysis',
      componentId: 'userListTable',
      name: '用户列表',
      type: 'table',
      displayInfo: '显示用户列表',
      description: '展示用户信息的表格',
      isActive: true,
      sortOrder: 2,
    },
    {
      id: '7',
      serviceName: 'msCentral',
      pageId: 'store-user-analysis',
      componentId: 'userSearchForm',
      name: '用户搜索表单',
      type: 'form',
      displayInfo: '用户搜索条件表单',
      description: '用于搜索和筛选用户的条件表单',
      isActive: true,
      sortOrder: 3,
    },
    {
      id: '8',
      serviceName: 'msCentral',
      pageId: 'group-business-data',
      componentId: 'dashboard',
      name: '业务数据仪表盘',
      type: 'dashboard',
      displayInfo: '业务数据总览',
      description: '展示业务数据的综合仪表盘',
      isActive: true,
      sortOrder: 1,
    },
    {
      id: '9',
      serviceName: 'msCentral',
      pageId: 'store-revenue-monitor',
      componentId: 'revparChart',
      name: 'RevPAR图表',
      type: 'chart',
      displayInfo: '显示RevPAR数据',
      description: '展示每间可售房收入图表',
      isActive: false,
      sortOrder: 3,
    },
    {
      id: '10',
      serviceName: 'msCentral',
      pageId: 'store-revenue-monitor',
      componentId: 'channelInfoTable',
      name: '渠道信息表',
      type: 'table',
      displayInfo: '显示渠道信息',
      description: '展示各渠道的详细信息表格',
      isActive: true,
      sortOrder: 4,
    },
  ];

  return mockComponents;
}

/**
 * 数据集配置
 */
export interface Dataset {
  id?: string;
  datasetId: string;
  name: string;
  description?: string;
  methodType: string;
  methodName: string;
  serviceName?: string;
  paramTemplate?: Record<string, any>;
  defaultParams?: Record<string, any>;
  dataFormat?: string;
  dataPath?: string;
  sampleData?: any;
  isActive?: boolean;
  fields?: DatasetField[];
  thinkingModes?: DatasetThinkingMode[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 数据集字段
 */
export interface DatasetField {
  id?: string;
  fieldId: string;
  fieldPath?: string;
  fieldName: string;
  fieldType: string;
  dataLevel?: number;
  parentPath?: string;
  isNested?: boolean;
  description?: string;
  isRequired?: boolean;
  defaultValue?: string;
  validation?: Record<string, any>;
  isSensitive?: boolean;
  maskRule?: string;
  maskPlaceholder?: string;
  maskPattern?: Record<string, any>;
  isEnum?: boolean;
  enumMapping?: Record<string, string>;
  sortOrder?: number;
}

/**
 * 组件配置
 */
export interface Component {
  id?: string;
  serviceName: string;
  pageId: string;
  componentId: string;
  name: string;
  type: string;
  parentId?: string;
  displayInfo?: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
  datasets?: ComponentDataset[];
  thinkingModes?: ComponentThinkingMode[];
}

/**
 * 组件数据集关联
 */
export interface ComponentDataset {
  datasetId: string;
  dataKey?: string;
  isPrimary?: boolean;
  useRenderedData?: boolean;
  paramOverrides?: Record<string, any>;
}

/**
 * 组件思考方式关联
 */
export interface ComponentThinkingMode {
  thinkingModeId: string;
  isDefault?: boolean;
  sortOrder?: number;
  promptTemplate?: string;
  includeFields?: string[];
  excludeFields?: string[];
  maskFields?: Record<string, string>;
  responseFormat?: ResponseFormat;
}

/**
 * 数据集思考方式关联
 */
export interface DatasetThinkingMode {
  thinkingModeId: string;
  isDefault?: boolean;
  sortOrder?: number;
  promptTemplate?: string;
  includeFields?: string[];
  excludeFields?: string[];
  maskFields?: Record<string, string>;
  responseFormat?: ResponseFormat;
}

/**
 * 思考方式配置
 */
export interface ThinkingMode {
  id: string;
  label: string;
  description?: string;
  category?: string;
  positiveWords?: string[];
  negativeWords?: string[];
  isActive?: boolean;
  sortOrder?: number;
  // 注意：不再直接关联返回格式，返回格式在关联时配置
}

/**
 * 返回格式
 */
export interface ResponseFormat {
  id?: string;
  formatId: string;  // 业务标识
  formatType: string;
  schema?: string;
  template?: string;
  requirements?: string;
  constraints?: Record<string, any>;
  maxLength?: number;
  positiveWords?: string[];
  negativeWords?: string[];
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export const aiConfigService = {
  /**
   * 获取数据集列表
   */
  async getDatasetList(): Promise<Dataset[]> {
    try {
      const response = await apiClient.get<ApiResponse<Dataset[]>>(
        '/api/ai/config/datasets'
      );
      if (response.data.code === 200) {
        return response.data.data || [];
      }
      return [];
    } catch (error: any) {
      // 如果接口不存在（404），返回空数组
      if (error.response?.status === 404) {
        console.warn('数据集列表接口未实现，返回空数组');
        return [];
      }
      throw error;
    }
  },

  /**
   * 获取数据集配置
   */
  async getDatasetConfig(datasetId: string): Promise<Dataset> {
    const response = await apiClient.get<ApiResponse<Dataset>>(
      `/api/ai/config/dataset/${datasetId}`
    );
    if (response.data.code === 200) {
      return response.data.data;
    }
    throw new Error(response.data.message || '获取配置失败');
  },

  /**
   * 创建或更新数据集
   */
  async upsertDataset(datasetData: Dataset): Promise<Dataset> {
    const response = await apiClient.post<ApiResponse<Dataset>>(
      '/api/ai/config/dataset',
      datasetData
    );
    if (response.data.code === 200) {
      return response.data.data;
    }
    throw new Error(response.data.message || '保存失败');
  },

  /**
   * 删除数据集
   */
  async deleteDataset(datasetId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/ai/config/dataset/${datasetId}`
    );
    if (response.data.code !== 200) {
      throw new Error(response.data.message || '删除失败');
    }
  },

  /**
   * 获取组件列表
   */
  async getComponentList(params?: {
    serviceName?: string;
    pageId?: string;
  }): Promise<Component[]> {
    try {
      const response = await apiClient.get<ApiResponse<Component[]>>(
        '/api/ai/config/components',
        { params }
      );
      if (response.data.code === 200) {
        return response.data.data || [];
      }
      // 如果没有数据，返回模拟数据
      return generateMockComponents();
    } catch (error: any) {
      if (error.response?.status === 404) {
        // 返回模拟数据
        return generateMockComponents();
      }
      // 其他错误也返回模拟数据，确保页面可以正常显示
      console.warn('获取组件列表失败，使用模拟数据:', error);
      return generateMockComponents();
    }
  },

  /**
   * 获取组件配置
   */
  async getComponentConfig(componentId: string): Promise<Component> {
    const response = await apiClient.get<ApiResponse<Component>>(
      `/api/ai/config/component/${componentId}`
    );
    if (response.data.code === 200) {
      return response.data.data;
    }
    throw new Error(response.data.message || '获取配置失败');
  },

  /**
   * 创建或更新组件
   */
  async upsertComponent(componentData: Component): Promise<Component> {
    const response = await apiClient.post<ApiResponse<Component>>(
      '/api/ai/config/component',
      componentData
    );
    if (response.data.code === 200) {
      return response.data.data;
    }
    throw new Error(response.data.message || '保存失败');
  },

  /**
   * 获取思考方式列表
   */
  async getThinkingModeList(): Promise<ThinkingMode[]> {
    try {
      const response = await apiClient.get<ApiResponse<ThinkingMode[]>>(
        '/api/ai/config/thinking-modes'
      );
      if (response.data.code === 200) {
        return response.data.data || [];
      }
      return [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  /**
   * 获取思考方式配置
   */
  async getThinkingModeConfig(thinkingModeId: string): Promise<ThinkingMode> {
    const response = await apiClient.get<ApiResponse<ThinkingMode>>(
      `/api/ai/config/thinking-mode/${thinkingModeId}`
    );
    if (response.data.code === 200) {
      return response.data.data;
    }
    throw new Error(response.data.message || '获取配置失败');
  },

  /**
   * 创建或更新思考方式
   */
  async upsertThinkingMode(thinkingModeData: ThinkingMode): Promise<ThinkingMode> {
    // TODO: 使用 mock 数据，后续替换为真实 API
    console.log('[Mock] 保存思考方式:', thinkingModeData);
    return thinkingModeData as ThinkingMode;
    
    // const response = await apiClient.post<ApiResponse<ThinkingMode>>(
    //   '/api/ai/config/thinking-mode',
    //   thinkingModeData
    // );
    // if (response.data.code === 200) {
    //   return response.data.data;
    // }
    // throw new Error(response.data.message || '保存失败');
  },

  /**
   * 获取返回格式列表（Mock）
   */
  async getResponseFormatList(): Promise<ResponseFormat[]> {
    // TODO: 使用 mock 数据，后续替换为真实 API
    return [
      {
        id: '1',
        formatId: 'markdown-standard',
        formatType: 'markdown',
        description: '标准 Markdown 格式',
        template: '# {title}\n\n{content}',
        maxLength: 2000,
        isActive: true,
      },
      {
        id: '2',
        formatId: 'json-simple',
        formatType: 'json',
        description: '简单 JSON 格式',
        schema: '{"type": "object"}',
        maxLength: 2000,
        isActive: true,
      },
      {
        id: '3',
        formatId: 'text-plain',
        formatType: 'text',
        description: '纯文本格式',
        maxLength: 2000,
        isActive: true,
      },
    ];
  },

  /**
   * 创建或更新返回格式（Mock）
   */
  async upsertResponseFormat(responseFormatData: ResponseFormat): Promise<ResponseFormat> {
    // TODO: 使用 mock 数据，后续替换为真实 API
    console.log('[Mock] 保存返回格式:', responseFormatData);
    return responseFormatData as ResponseFormat;
  },
};
